import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, Search, Plus, ShoppingCart, PhoneCall, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { fmtErr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useLang, LangToggle } from "@/lib/i18n";
import { UNIT_OPTIONS, unitLabel, inr, toMt, fmtDate } from "@/lib/utils";

export default function Fertilizer() {
  const { t } = useLang();
  const { items, addItem, removeItem, count } = useCart();
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogProduct, setDialogProduct] = useState(null);
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("MT");
  const [busy, setBusy] = useState(false);
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    api.get("/public/business").then((r) => setBusiness(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/companies")
      .then((r) => setCompanies(r.data))
      .catch((e) => toast.error(fmtErr(e)));
  }, []);

  const openCompany = async (company) => {
    setSelected(company);
    setSearch("");
    try {
      const { data } = await api.get("/products", { params: { company_id: company.id } });
      setProducts(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openDialog = (p) => {
    setDialogProduct(p);
    setQty("");
    setUnit("MT");
  };

  const mtPreview = dialogProduct ? toMt(qty || 0, unit, dialogProduct.truck_capacity_mt) : 0;
  const stockError = dialogProduct && mtPreview > Number(dialogProduct.stock_mt || 0) + 1e-9;
  const lineTotal = dialogProduct && dialogProduct.price_per_mt != null ? dialogProduct.price_per_mt * mtPreview : null;

  const addToOrder = () => {
    if (!qty || Number(qty) <= 0) {
      toast.error("Enter a quantity");
      return;
    }
    if (stockError) {
      toast.error("Requested quantity exceeds available stock");
      return;
    }
    addItem({
      product_id: dialogProduct.id,
      company_id: selected.id,
      company_name: selected.name,
      product_name: dialogProduct.name,
      qty: Number(qty),
      unit,
      truck_capacity_mt: dialogProduct.truck_capacity_mt,
      rate_per_mt: dialogProduct.price_per_mt,
      stock_mt: dialogProduct.stock_mt,
    });
    toast.success(`${dialogProduct.name} (${qty} ${unitLabel(unit)}) added to order`);
    setDialogProduct(null);
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
  const cartTotal = items.reduce((s, i) => s + (i.rate_per_mt != null ? i.rate_per_mt * toMt(i.qty, i.unit, i.truck_capacity_mt) : 0), 0);

  if (!selected) {
    return (
      <div className="space-y-6" data-testid="fertilizer-company-view">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("selectCompany")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("selectCompanySub")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c, i) => (
            <div key={c.id} className="sf-card p-6 flex items-center justify-between" data-testid={`company-card-${i}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#0B192C]/5 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-[#1E3E62]" />
                </div>
                <span className="font-display font-bold text-slate-900">{c.name}</span>
              </div>
              <button data-testid={`view-products-btn-${i}`} className="sf-link text-sm" onClick={() => openCompany(c)}>
                {t("viewProducts")}
              </button>
            </div>
          ))}
          {companies.length === 0 && <div className="text-sm text-slate-400">{t("noCompanies")}</div>}
        </div>
        <LangToggle />
        <CartBar items={items} count={count} total={cartTotal} onRemove={removeItem} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fertilizer-product-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button data-testid="back-to-companies-btn" className="sf-link text-sm inline-flex items-center gap-1" onClick={() => setSelected(null)}>
            <ArrowLeft className="h-4 w-4" /> {t("allCompanies")}
          </button>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">{selected.name} — {t("products")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="product-search-input"
              className="sf-input pl-9 w-64"
              placeholder={t("searchFertilizer")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <LangToggle />
        </div>
      </div>

      <div className="sf-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th className="sf-th">{t("product")}</th>
              <th className="sf-th">{t("availableStock")}</th>
              <th className="sf-th">{t("ratePerMt")}</th>
              <th className="sf-th text-right">{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const out = Number(p.stock_mt || 0) <= 0;
              return (
                <tr key={p.id} className="sf-tr" data-testid={`product-row-${p.id}`}>
                  <td className="sf-td font-semibold text-slate-900">{p.name}</td>
                  <td className="sf-td">
                    {out ? (
                      <span className="rounded-full bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold px-2.5 py-0.5">{t("outOfStock")}</span>
                    ) : (
                      <span className="text-slate-800">{p.stock_mt} MT</span>
                    )}
                  </td>
                  <td className="sf-td">
                    {p.price_per_mt != null ? (
                      <span className="font-semibold">{inr(p.price_per_mt)} / MT</span>
                    ) : (
                      <a href={business?.customer_contact_number ? `tel:+91${business.customer_contact_number}` : "#"} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-2.5 py-1" data-testid={`call-for-rate-${p.id}`}>
                        <PhoneCall className="h-3.5 w-3.5" /> {t("callForRate")}
                      </a>
                    )}
                  </td>
                  <td className="sf-td text-right">
                    <button data-testid={`add-product-btn-${p.id}`} className="sf-btn-blue !py-1.5 !px-3.5" disabled={out} onClick={() => openDialog(p)}>
                      <Plus className="h-4 w-4" /> {t("add")}
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td className="sf-td text-slate-400" colSpan={4}>
                  {t("noProducts")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialogProduct} onOpenChange={(o) => !o && setDialogProduct(null)}>
        <DialogContent className="max-w-md bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="qty-dialog">
          <DialogHeader>
            <DialogTitle>{t("addQuantity")}</DialogTitle>
          </DialogHeader>
          {dialogProduct && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                <div className="font-semibold text-slate-900">{dialogProduct.name}</div>
                <div className="text-slate-500 mt-0.5">
                  {selected.name} · {t("available")}: <span className="font-semibold text-slate-800">{dialogProduct.stock_mt} MT</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="sf-label">{t("quantity")}</label>
                  <input
                    data-testid="qty-input"
                    type="number"
                    min="0"
                    step="any"
                    className="sf-input"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder="e.g. 2"
                  />
                </div>
                <div>
                  <label className="sf-label">{t("unit")}</label>
                  <div className="flex gap-1.5">
                    {UNIT_OPTIONS.map((u) => (
                      <button
                        key={u.value}
                        data-testid={`unit-option-${u.value}`}
                        onClick={() => setUnit(u.value)}
                        className={`flex-1 rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                          unit === u.value ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                        }`}
                      >
                        {u.value === "KG" ? t("kg") : u.value === "MT" ? t("metricTon") : t("truck")}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-sm text-slate-600" data-testid="qty-mt-preview">
                = <span className="font-bold text-slate-900">{mtPreview.toLocaleString("en-IN", { maximumFractionDigits: 3 })} MT</span>
                {dialogProduct.price_per_mt != null && <> · {t("lineTotal")}: <span className="font-bold">{inr(lineTotal)}</span></>}
              </div>
              {stockError && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5" data-testid="qty-stock-error">Requested quantity exceeds available stock ({dialogProduct.stock_mt} MT)</div>}
              <DialogFooter>
                <button className="sf-btn-outline" onClick={() => setDialogProduct(null)}>
                  {t("cancel")}
                </button>
                <button data-testid="add-to-order-btn" className="sf-btn-primary" onClick={addToOrder}>
                  {t("addToOrder")}
                </button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CartBar items={items} count={count} total={cartTotal} onRemove={removeItem} />
    </div>
  );
}

function CartBar({ items, count, total, onRemove }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div className="sticky bottom-4 z-20" data-testid="cart-bar">
      {open && (
        <div className="sf-card p-4 mb-2 shadow-md" data-testid="cart-panel">
          <div className="font-display font-bold text-slate-900 text-sm mb-1">{t("cartPanelTitle")}</div>
          <div className="divide-y divide-slate-100">
            {items.map((i) => (
              <div key={i.product_id} className="flex items-center justify-between py-2 text-sm" data-testid={`cart-item-${i.product_id}`}>
                <div>
                  <span className="font-semibold text-slate-900">{i.product_name}</span>
                  <span className="text-slate-500">
                    {' '}· {i.company_name} · {i.qty} {unitLabel(i.unit)}
                  </span>
                </div>
                <button data-testid={`cart-item-remove-${i.product_id}`} className="text-rose-600 hover:text-rose-800 p-1" onClick={() => onRemove(i.product_id)} title="Remove from order">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="sf-card p-4 flex items-center justify-between shadow-md">
        <button className="flex items-center gap-3 text-sm" data-testid="cart-toggle-btn" onClick={() => setOpen(!open)}>
          <span className="relative">
            <ShoppingCart className="h-5 w-5 text-[#1E3E62]" />
            <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="cart-count">
              {count}
            </span>
          </span>
          <span className="font-semibold text-slate-900">
            {count} {t("productsInOrder")}
          </span>
          {total > 0 && <span className="text-slate-500">· {inr(total)} (priced lines)</span>}
          <span className="text-slate-400 text-xs font-medium">{open ? t("hide") : t("viewRemove")}</span>
        </button>
        <Link to="/customer/review" data-testid="review-order-btn" className="sf-btn-primary">
          {t("reviewOrder")} <ShoppingCart className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
